<template>
	<div class="tw-flex tw--mx-20">
		<div class="tw-w-3/5 px-2">
			<div class="tw-mb-3">
				<c-job-form
					@jobAdded="onJobNameAdded"
					@select-group="filterJobs"
					@downloadReportExcel="downloadReportExcel"
					@downloadReportPDF="downloadReportPDF"
					:add-area="addArea"
					:job-list="jobList"
					:reference-group="groupId"
					:max-jobs-list="-1"
					:groups="groups"
					:disable-add="indexTab === 1"
				/>
			</div>
		<div>
			<c-job-list
					:job-list="filteredJobsE"
					:selected-job="selectedJob"
					:jobs-deleted="jobsDeleted"
					:index-tab="indexTab"
					@deleteClick="onDeleteClick"
					@descriptionClick="onDescriptionClick"
					@editionClick="onEditionClick"
					@viewPdfClick="onPreviewPDF"
					@filterJobByStatus="filterJobByStatus"
					@onChangeStatus="changeStatus"
					@saveDeleted="deleteJobsSelected"
				/>
			</div>
		</div>
		<div class="tw-w-2/5 px-2">
			<c-job-selector
				v-show="jobIsSelected"
				:selected-prev="selectedPrev"
				:selected-next="selectedNext"
				:selected-job="selectedJob"
				@selectedJobDataChanged="onSelectedJobDataChanged"
				@selectedJobDataChangedName="selectedJobDataChangedName"
				@prevClick="onPrevClick"
				@nextClick="onNextClick"
				@infoClick="onInfoClick"
				@addNewArea="addNewArea"
				:job-list-navigation="true"
				:show-info-icon="true"
				ref="jobSelector"
			>
				<template slot="action-footer">
					<v-btn
						:disabled="!selectionIsReady || isSaving"
						:loading="isSaving"
						class="custom-button tw-px-8"
						color="primary"
						@click="onGenerateHierarchyClick"
						>{{ isSaving ? 'Guardando...' : 'Guardar' }}</v-btn
					>
					<p v-if="!selectionIsReady" class="tw-mt-2 tw-mb-0">
						{{
							totalJobsCount === 0
								? 'Agrega al menos un puesto de trabajo para continuar'
								: isEditing
									? 'Completa los verbos y la configuración de niveles para guardar'
									: 'Configura al menos un puesto de trabajo para guardar'
						}}
					</p>
				</template>
			</c-job-selector>
			<c-model-instructions v-show="!jobIsSelected" />
		</div>
	</div>
</template>

<script>
import cJobForm from '@/modules/job-profile/components/jobs-profile/job-form';
import cJobList from '@/modules/job-profile/components/jobs-profile/job-list';
import cModelInstructions from '@/modules/job-profile/components/jobs-profile/model-instructions';
import cJobSelector from '@/modules/job-profile/components/jobs-profile/job-selector';
import { filter, isEmpty, findIndex, propEq, prop, isNil, pathOr, clone } from 'ramda';
import { mapState } from 'vuex';
import {
	generateJob,
	verifyJobIsCompleted,
	calculateJobProgress,
} from '@/modules/job-profile/helpers/helpers';
import helper from '@/shared/helper';

const { generateBlob, showDownloadDialog } = helper;

function created() {
	this.filterJobs();
}

async function downloadReportExcel({ type, contentType }) {
	const body = {
		simple: false,
		jobIds: this.idsFilterJobs.length !== 0 ? this.idsFilterJobs : null,
	};
	try {
		const { data: pdfData } = await this.$httpPraktiko.post('reports/job-profile/excel', body, {
			responseType: 'blob',
		});
		const blob = generateBlob(pdfData, contentType);
		showDownloadDialog(blob, 'descripcion-puesto-general', type);
		this.showNotification('Reporte Generado Exitosamente');
	} catch (error) {
		this.showGenericError();
	}
}

async function downloadReportPDF(contentType) {
	const body = {
		simple: false,
		jobIds: this.idsFilterJobs.length !== 0 ? this.idsFilterJobs : null,
	};
	try {
		const code = 'general-profile';
		const { data: pdfData } = await this.$httpPraktiko.post(
			`reports/job-profile-general/${code}/pdf`,
			body,
			{
				responseType: 'blob',
			},
		);
		const blob = generateBlob(pdfData, contentType);
		showDownloadDialog(blob, 'descripcion-puesto-general', 'pdf');
	} catch (error) {
		this.showGenericError();
	}
}

export default {
	name: 'page-new-profile',
	components: {
		cJobForm,
		cJobList,
		cModelInstructions,
		cJobSelector,
	},
	async beforeCreate() {
		this.$store.dispatch('recruitment/flagDisabledButton', false);

		try {
			this.$store.dispatch('jobProfile/clearJobDescriptionCache');

			await Promise.all([
				this.$store.dispatch('jobEvaluation/getGuidelineModelStatus'),
				this.$store.dispatch('jobEvaluation/loadGroups'),
				this.$store.dispatch('jobEvaluation/loadJobEvaluationConfig'),
			]);
		} catch (error) {
			this.showGenericError('Error en la carga de datos');
		}
	},
	created,
	data() {
		return {
			selectedJob: {},
			groupId: null,
			filteredJobsE: [],
			indexTab: null,
			addArea: false,
			isSaving: false,
			lastSaveAttempt: 0,
			isLoadingJobs: true, // Estado de loading para job-list
		};
	},
	methods: {
		// ✅ CARGA BAJO DEMANDA - Cargar datos de jobProfile solo cuando se necesiten
		async loadJobProfileDataIfNeeded() {
			try {
				// NO mostrar loading en job-list, solo un indicador local si es necesario
				// this.isLoadingJobs = true; // ❌ Esto causa que se recargue la lista

				// Cargar datos necesarios para job-selector en paralelo
				await Promise.all([
					// Datos de jobProfile
					this.$store.dispatch('jobProfile/loadJobProfileConfig'),
					this.$store.dispatch('jobProfile/loadKnowledges'),
					this.$store.dispatch('jobProfile/loadCareers'),
					this.$store.dispatch('jobProfile/loadTimes'),
					this.$store.dispatch('jobProfile/loadMeasureOfTime'),
					this.$store.dispatch('jobProfile/loadResponsabilities'),
					this.$store.dispatch('jobProfile/loadRelevance'),

					// Datos de jobEvaluation necesarios para job-selector
					this.$store.dispatch('jobEvaluation/loadJobEvaluationConfig'),
					this.$store.dispatch('jobEvaluation/loadRoles'),
					this.$store.dispatch('jobEvaluation/loadTitles'),
					this.$store.dispatch('jobEvaluation/loadExperiences'),
					this.$store.dispatch('jobEvaluation/loadEnglishLevels'),
					this.$store.dispatch('jobEvaluation/loadVerbs'), // ✅ CRÍTICO para verbos
				]);
			} catch (error) {
				// console.error('Error cargando datos para edición de job:', error);
				this.showGenericError('Error al cargar datos para edición de puesto');
			}
			// ❌ No cambiar isLoadingJobs aquí
		},

		onJobNameAdded(jobName) {
			this.showNotification(
				'RECUERDA: Termina de configurar el puesto y GUÁRDALO para proceder con la Descripción de Puesto',
				'warning',
			);
			this.$store.dispatch('jobEvaluation/updateJobList', [...this.jobList, generateJob(jobName)]);
		},
		onDeleteClick(selectedJob) {
			if (this.filteredJobsE.length > 0 && selectedJob.id) {
				const jobDelete = filter(job => job.name === selectedJob.name, this.filteredJobsE);
				const filterJobDelete = filter(job => job.name !== selectedJob.name, this.filteredJobsE);
				const jobList = filter(job => job.name !== jobDelete.name, this.jobList);
				const jobList2 = filter(job => job.name !== selectedJob.name, this.jobList);
				this.$store.dispatch('jobEvaluation/updateJobList', jobList);
				this.$store.dispatch('jobEvaluation/updateJobList', jobList2);
				this.$store.dispatch('jobEvaluation/updateFilterJobList', filterJobDelete);
				this.$store.dispatch('jobEvaluation/jobsDeleteinJobList', jobDelete);
				this.deleteJobsSelected();
			} else if (selectedJob.id) {
				const jobList = filter(job => job.name !== selectedJob.name, this.jobList);
				const jobListDeleted = filter(job => job.name === selectedJob.name, this.jobList);
				this.$store.dispatch('jobEvaluation/updateJobList', jobList);
				this.$store.dispatch('jobEvaluation/jobsDeleteinJobList', jobListDeleted);
				this.deleteJobsSelected();
			} else {
				const jobList = filter(job => job.name !== selectedJob.name, this.jobList);
				this.$store.dispatch('jobEvaluation/updateJobList', jobList);
			}
		},
		onDescriptionClick(selectJob) {
			this.selectedJob = selectJob;
			this.$store.dispatch('jobProfile/saveJobSelected', this.selectedJob);
			this.goTo('job-mission', { params: { id: selectJob.id, job: this.selectedJob } });
		},
		async onEditionClick(selectedJob) {
			this.selectedJob = selectedJob;

			// ✅ CARGA BAJO DEMANDA - Solo cuando se selecciona un job para editar
			await this.loadJobProfileDataIfNeeded();

			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
			this.$refs.jobSelector.prevLocalPage();
		},
		onPreviewPDF(selectedJob) {
			this.$store.dispatch('jobProfile/saveJobSelected', selectedJob);
			this.goTo('job-view-details', { params: { id: selectedJob.id } });
		},
		onInfoClick() {
			this.selectedJob = {};
			// ✅ LIMPIAR LOCALSTORAGE - Quitar selección
			this.$store.dispatch('jobProfile/saveJobSelected', {});
		},
		onNextClick() {
			if (!isNil(this.selectedNext)) {
				this.selectedJob = this.selectedNext;
				// ✅ ACTUALIZAR LOCALSTORAGE - Mantener navegación sincronizada
				this.$store.dispatch('jobProfile/saveJobSelected', this.selectedNext);
			}
		},
		onPrevClick() {
			if (!isNil(this.selectedPrev)) {
				this.selectedJob = this.selectedPrev;
				// ✅ ACTUALIZAR LOCALSTORAGE - Mantener navegación sincronizada
				this.$store.dispatch('jobProfile/saveJobSelected', this.selectedPrev);
			}
		},
		onSelectedJobDataChanged(job) {
			const ai = (job && job.additionalInformation) || {};
			const jr = ai.jobHeReplaces ? ai.jobHeReplaces.map(j => j.name) : 'N/A';
			// eslint-disable-next-line no-console
			console.log('[Parent] jobHeReplaces:', jr);
			const modifiedJob = clone(job);
			modifiedJob.completed = verifyJobIsCompleted(job);
			modifiedJob.progress = calculateJobProgress(job);
			this.selectedJob = modifiedJob;

			// ✅ ACTUALIZAR LOCALSTORAGE - Mantener datos frescos
			this.$store.dispatch('jobProfile/saveJobSelected', modifiedJob);
			this.$store.dispatch('jobEvaluation/updateJobItem', modifiedJob);
		},
		selectedJobDataChangedName(job) {
			const modifiedJob = clone(job);
			modifiedJob.completed = verifyJobIsCompleted(job);
			modifiedJob.progress = calculateJobProgress(job);
			this.selectedJob = modifiedJob;

			// ✅ ACTUALIZAR LOCALSTORAGE - Mantener datos frescos
			this.$store.dispatch('jobProfile/saveJobSelected', modifiedJob);
			this.$store.dispatch('jobEvaluation/updateJobItem', modifiedJob);
		},
		onGenerateHierarchyClick() {
			// Prevenir múltiples clics simultáneos
			// if (this.isSaving) {
			// 	console.warn('Operación de guardado ya en progreso');
			// 	return;
			// }

			// Implementar debounce para evitar clics accidentales múltiples
			const now = Date.now();
			// if (now - this.lastSaveAttempt < 2000) {
			// 	// 2 segundos de debounce
			// 	console.warn('Intento de guardado muy frecuente, ignorando');
			// 	return;
			// }
			this.lastSaveAttempt = now;

			// Validación: el puesto editado debe estar completo, o al menos 1 configurado
			if (!this.selectionIsReady) {
				const errorMsg = this.isEditing
					? 'Completa los verbos y la configuración de niveles del puesto actual antes de guardar'
					: 'Debe haber al menos un puesto completamente configurado para guardar';
				this.showGenericError(errorMsg);
				return;
			}

			// Verificar que la lista de trabajos no esté vacía
			if (!this.jobList || this.jobList.length === 0) {
				// console.warn('Intento de guardar sin puestos de trabajo');
				this.showGenericError('Debe agregar al menos un puesto de trabajo');
				return;
			}

			// Activar estado de carga
			this.isSaving = true;

			// DEBUG: Ver qué hay en el store antes de enviar
			const jobInStore = this.jobList.find(
				j => j.id === this.selectedJob.id
					|| j.name === this.selectedJob.name,
			) || {};
			const sAi = (jobInStore && jobInStore.additionalInformation) || {};
			const sJr = sAi.jobHeReplaces
				? sAi.jobHeReplaces.map(j => j.name) : 'N/A';
			// eslint-disable-next-line no-console
			console.log('[SAVE] store jobHeReplaces:', sJr);
			const jAi = (this.selectedJob
				&& this.selectedJob.additionalInformation) || {};
			const jJr = jAi.jobHeReplaces
				? jAi.jobHeReplaces.map(j => j.name) : 'N/A';
			// eslint-disable-next-line no-console
			console.log('[SAVE] selectedJob jobHeReplaces:', jJr);

			this.$store
				.dispatch('jobEvaluation/assignHierarchy', { onlyCompleted: true })
				.then(() => {
					this.showNotification('Puesto agregado correctamente');
					this.$store.dispatch('jobEvaluation/getGuidelineModelStatus');
					// window.location.reload();
					this.selectedJob = {};
				})
				.catch((e) => {
					// console.error('Error al guardar:', e);

					// Manejo específico de errores de concurrencia
					if (e.response && (e.response.status === 409 || e.response.status === 500)) {
						// Error de concurrencia o conflicto
						if (e.response.status === 409) {
							this.showGenericError(
								'Otro usuario está realizando cambios al mismo tiempo. Por favor, espera un momento e intenta nuevamente.',
							);
						} else if (
							e.response.status === 500 &&
							e.response.data &&
							e.response.data.message &&
							(e.response.data.message.includes('concurrent') ||
								e.response.data.message.includes('lock'))
						) {
							this.showGenericError(
								'El sistema está procesando otros cambios. Por favor, espera un momento e intenta nuevamente.',
							);
						} else {
							// Manejo de errores específicos existentes
							const messageErrorJob = (e.data && e.data.message) ||
								(e.response && e.response.data && e.response.onSelectedJobDataChangedName.message);
							if (messageErrorJob) {
								const messageSplit = messageErrorJob.split(' - ');
								if (messageSplit[0] === 'NAME_JOBS_DUPLICATED') {
									this.showGenericError(`El/Los Puesto/s ${messageSplit[1]} ya han sido creados`);
									this.$store.dispatch('jobEvaluation/getGuidelineModelStatus');
								} else {
									this.showGenericError(
										'Error al guardar los cambios. Por favor, intenta nuevamente.',
									);
								}
							} else {
								this.showGenericError(
									'Error al guardar los cambios. Por favor, intenta nuevamente.',
								);
							}
						}
					} else {
						// Otros errores
						const messageErrorJob = (e.data && e.data.message)
							|| (e.response && e.response.data && e.response.data.message);
						if (messageErrorJob) {
							const messageSplit = messageErrorJob.split(' - ');
							if (messageSplit[0] === 'NAME_JOBS_DUPLICATED') {
								this.showGenericError(`El/Los Puesto/s ${messageSplit[1]} ya han sido creados`);
								this.$store.dispatch('jobEvaluation/getGuidelineModelStatus');
							} else {
								this.showGenericError(
									'Error al guardar los cambios. Por favor, intenta nuevamente.',
								);
							}
						} else {
							this.showGenericError('Error al guardar los cambios. Por favor, intenta nuevamente.');
						}
					}
				})
				.finally(() => {
					// Desactivar estado de carga
					this.isSaving = false;
				});
		},
		filterJobs(groupId) {
			this.groupId = groupId;
			this.$store.dispatch('jobEvaluation/filterJobsByGroupId', groupId);
		},
		downloadReportExcel,
		downloadReportPDF,
		async deleteJobsSelected() {
			try {
				this.$store.dispatch('jobEvaluation/assignHierarchy');
				const idJobsDeleted = this.jobsDeleted.map(f => f.id);
				const join = idJobsDeleted.join(',');
				const params = {
					jobIds: join,
				};
				await this.$httpPraktiko.delete('job-description/delete', { params });
				this.showNotification('Puestos Eliminados Correctamente');
				const jobFilter = this.jobList.filter(job => !idJobsDeleted.includes(job.id));
				this.$store.dispatch('jobEvaluation/updateJobList', jobFilter);
				this.$store.commit('jobEvaluation/CLEAN_DELETE_DATA');
			} catch (error) {
				this.showGenericError();
			}
		},
		changeStatus({ status, selectedJob }) {
			const modifiedJob = clone(selectedJob);
			modifiedJob.status = status;
			modifiedJob.completed = verifyJobIsCompleted(modifiedJob);
			modifiedJob.progress = calculateJobProgress(modifiedJob);
			// 1. Actualizar store local
			this.$store.dispatch('jobEvaluation/updateJobItem', modifiedJob);
			// 2. Persistir al backend — solo jobs configurados (tienen verbsCode)
			this.$store.dispatch('jobEvaluation/assignHierarchy', { onlyCompleted: true });
			this.selectedJob = {};
		},
		filterJobByStatus(index) {
			this.indexTab = index;
			this.groupId = [];
			if (index === 0) {
				this.filteredJobsE = this.jobList.filter(job => job.status === true);
			} else if (index === 1) {
				this.filteredJobsE = this.jobList.filter(job => job.status === false);
			} else if (index === 2) {
				this.filteredJobsE = clone(this.jobList);
			}
			this.$store.dispatch('jobEvaluation/filterJobActive', this.filteredJobsE);
		},
		addNewArea(add) {
			this.addArea = add;
		},
	},
	computed: {
		...mapState('jobEvaluation', {
			jobList: pathOr([], ['supplies', 'guidelineModel', 'userForm', 'jobList']),
			filteredJobs: pathOr([], ['supplies', 'guidelineModel', 'userForm', 'filteredJobs']),
			jobsDeleted: pathOr([], ['supplies', 'guidelineModel', 'userForm', 'deletedJobs']),
			idsFilterJobs: pathOr(null, ['supplies', 'guidelineModel', 'userForm', 'idsFilterJobs']),
			groups: pathOr([], ['supplies', 'guidelineModel', 'groups']), // Acceso centralizado a grupos
		}),
		jobIsSelected() {
			return !isEmpty(this.selectedJob);
		},
		selectedIndex() {
			return findIndex(propEq('name', this.selectedJob.name), this.jobList);
		},
		selectedNext() {
			return prop(this.selectedIndex + 1, this.jobList);
		},
		selectedPrev() {
			return this.selectedIndex - 1 > -1 ? prop(this.selectedIndex - 1, this.jobList) : undefined;
		},
		isEditing() {
			return !isEmpty(this.selectedJob);
		},
		selectionIsReady() {
			if (!this.jobList || this.jobList.length === 0) {
				return false;
			}

			// Si estamos editando un puesto → validar SOLO ese puesto (verbos + niveles)
			if (this.isEditing) {
				return verifyJobIsCompleted(this.selectedJob);
			}

			// Si NO estamos editando → al menos 1 puesto configurado
			return this.jobList.some(job => job.completed);
		},
		incompleteJobsCount() {
			if (!this.jobList || this.jobList.length === 0) {
				return 0;
			}
			return this.jobList.filter(job => !job.completed).length;
		},
		totalJobsCount() {
			return this.jobList ? this.jobList.length : 0;
		},
	},
	watch: {
		filteredJobs: {
			handler(newVal) {
				this.filteredJobsE = newVal;
				if (this.jobsDeleted.length !== 0) {
					this.filteredJobsE = this.filteredJobsE.filter(nV =>
						this.jobsDeleted.some(jD => jD.id !== nV.id),
					);
				}
			},
		},
		jobsDeleted: {
			handler(newVal) {
				if (newVal.length > 0) {
					const idJobsDeleted = newVal.map(f => f.id);
					const jobFilter = this.jobList.filter(job => !idJobsDeleted.includes(job.id));
					this.$store.dispatch('jobEvaluation/updateJobList', jobFilter);
				}
			},
		},
		jobList: {
			handler(newVal, oldVal) {
				// Solo desactivar loading cuando realmente lleguen datos del servidor
				// No cuando sea la inicialización vacía
				if (newVal && newVal.length > 0) {
					this.isLoadingJobs = false;
					// Respetar el tab actual, si no hay tab definido → Activos (0)
					const currentTab = this.indexTab !== null ? this.indexTab : 0;
					this.filterJobByStatus(currentTab);
				} else if (oldVal && oldVal.length > 0 && newVal.length === 0) {
					// Si había datos y ahora no hay, mantener loading activo
					this.isLoadingJobs = true;
				}
			},
		},
	},
};
</script>

<style lang="scss" scoped>
.section-content-grid {
	&-left-content {
		width: 60%;
	}
	&-right-content {
		width: 40%;
	}
}
</style>



// WEBPACK FOOTER //
// src/modules/job-profile/pages/page-new-profile.vue